import React, { useEffect, useRef, useState } from 'react';
import './EmailCreate.scss';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import WifiIcon from '@mui/icons-material/Wifi';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';

const SOCIAL_OUTLINE_SVGS = {
  linkedin:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>',
  facebook:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>',
  twitter:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="#000000"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
  instagram:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>',
  youtube:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 4v16l13-8z"/></svg>',
};
const EMAIL_BASE_WIDTH = 601;
const MOBILE_CONTENT_WIDTH = 260;
const MOBILE_SCALE = MOBILE_CONTENT_WIDTH / EMAIL_BASE_WIDTH;
const DESKTOP_BODY_HEIGHT = 450;
const MOBILE_BODY_HEIGHT = 390;
const SCROLL_BAR_TRACK_PADDING = 12;

const TemplatePreview = ({ template, viewType = 'desktop' }) => {
  const scrollRef = useRef(null);
  const bodyContainerRef = useRef(null);
  const isDesktop = viewType === 'desktop';
  const looksLikeMobile = !isDesktop;
  const [timeString, setTimeString] = useState('');
  const [contentHeight, setContentHeight] = useState(0);
  const [bodyContainerWidth, setBodyContainerWidth] = useState(0);

  const handleIframeLoad = (e) => {
    const iframe = e.target;
    if (iframe && iframe.contentWindow && iframe.contentWindow.document.body) {
      const height = iframe.contentWindow.document.body.scrollHeight;
      setContentHeight(height);
    }
  };

  const thumbRef = useRef(null);
  const [hasVerticalScroll, setHasVerticalScroll] = useState(false);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollHeight > clientHeight !== hasVerticalScroll) {
      setHasVerticalScroll(scrollHeight > clientHeight);
    }

    if (!thumbRef.current) return;
    const trackHeight = clientHeight - SCROLL_BAR_TRACK_PADDING;
    const thumbHeight = Math.max(20, (clientHeight / scrollHeight) * trackHeight);
    const scrollableRange = scrollHeight - clientHeight;
    const thumbRange = trackHeight - thumbHeight;

    const thumbTop = scrollableRange > 0 ? (scrollTop / scrollableRange) * thumbRange : 0;

    thumbRef.current.style.height = `${thumbHeight}px`;
    thumbRef.current.style.top = `${thumbTop + 6}px`;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (scrollRef.current) {
        handleScroll({ target: scrollRef.current });
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [contentHeight, viewType]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'iframeHeight') {
        setContentHeight(event.data.height);
      }
    };

    window.addEventListener('message', handleMessage);

    const formatTime = (date) => {
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const hh = hours < 10 ? `0${hours}` : `${hours}`;
      const mm = minutes < 10 ? `0${minutes}` : `${minutes}`;
      return `${hh}:${mm}`;
    };
    setTimeString(formatTime(new Date()));
    const intervalId = setInterval(() => {
      setTimeString(formatTime(new Date()));
    }, 60 * 1000);
    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(intervalId);
    };
  }, []);

  const getMuiIconSvg = (name) => {
    return SOCIAL_OUTLINE_SVGS[name] || '';
  };

  const cleanupHtmlContent = (htmlString) => {
    if (!htmlString) return '';
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlString;
      tempDiv
        .querySelectorAll('.q-del, button.q-del, button[class*="delete"]')
        .forEach((btn) => btn.remove());
      tempDiv.querySelectorAll('.q-button, .q-comp[data-comp="button"]').forEach((btn) => {
        const bgColor = btn.style.getPropertyValue('--btn-bg') || '#7a6a55';
        const textColor = btn.style.getPropertyValue('--btn-color') || '#ffffff';
        const radius = btn.style.getPropertyValue('--btn-radius') || '10px';
        const anchor = btn.querySelector('.q-btn-inner');
        if (anchor) {
          anchor.style.backgroundColor = bgColor;
          anchor.style.color = textColor;
          anchor.style.borderRadius = radius;
          anchor.style.padding = '12px 24px';
          anchor.style.display = 'inline-block';
          anchor.style.textDecoration = 'none';
          anchor.style.fontWeight = '500';
        }
      });
      tempDiv.querySelectorAll('.q-social, .q-comp[data-comp="social"]').forEach((social) => {
        const iconColor = social.style.getPropertyValue('--social-icon-color') || '#EED191';
        const glyphColor = social.style.getPropertyValue('--social-icon-glyph') || '#fff';
        social.querySelectorAll('.q-social-ic').forEach((icon) => {
          icon.style.backgroundColor = iconColor;
          icon.style.color = glyphColor;
          icon.style.width = '28px';
          icon.style.height = '28px';
          icon.style.borderRadius = '50%';
          icon.style.display = 'inline-flex';
          icon.style.alignItems = 'center';
          icon.style.justifyContent = 'center';
        });
      });

      tempDiv.querySelectorAll('.q-social-svg[data-name]').forEach((slot) => {
        const name = slot.dataset.name;
        const svg = getMuiIconSvg(name);
        if (svg && !slot.innerHTML.includes('<svg')) {
          slot.innerHTML = svg;
          const svgEl = slot.querySelector('svg');
          if (svgEl) {
            svgEl.style.width = '14px';
            svgEl.style.height = '14px';
          }
        }
      });

      tempDiv.querySelectorAll('*').forEach((el) => {
        el.classList.remove(
          'selected',
          'editing',
          'dragging',
          'resizing',
          'q-selected',
          'q-dragging',
          'resizer',
        );
        el.removeAttribute('data-selected');
        el.removeAttribute('data-editing');
        el.removeAttribute('contenteditable');
        el.removeAttribute('data-allow-overlap');
        el.removeAttribute('draggable');

        const greenHexes = [
          '#29e67c',
          '#59e391',
          '#03e67c',
          '#1fc06c',
          'rgb(41, 230, 124)',
          'rgb(89, 227, 145)',
        ];
        const border = el.style.border || '';
        const boxShadow = el.style.boxShadow || '';
        const borderColor = el.style.borderColor || '';

        if (greenHexes.some((hex) => border.toLowerCase().includes(hex.toLowerCase()))) {
          el.style.border = 'none';
        }
        if (greenHexes.some((hex) => boxShadow.toLowerCase().includes(hex.toLowerCase()))) {
          el.style.boxShadow = 'none';
        }
        if (greenHexes.some((hex) => borderColor.toLowerCase().includes(hex.toLowerCase()))) {
          el.style.borderColor = 'transparent';
        }
        if (border.includes('dashed')) {
          el.style.border = 'none';
        }

        if (el.classList.contains('ql-editor')) {
          el.style.border = 'none';
          el.style.padding = '0';
          el.style.margin = '0';
          el.style.height = 'auto';
        }
      });

      tempDiv.querySelectorAll('table').forEach((el) => {
        el.style.borderCollapse = 'collapse';
        el.style.border = 'none';
        if (el.getAttribute('width') === '100%') {
          el.style.width = '100%';
        } else if (el.getAttribute('width')) {
          el.style.width = el.getAttribute('width') + 'px';
        }
      });
      tempDiv.querySelectorAll('td').forEach((el) => {
        el.style.border = 'none';
        if (el.getAttribute('width')) {
          el.style.width = el.getAttribute('width') + 'px';
        }
      });

      let finalHtml = tempDiv.innerHTML || htmlString;
      finalHtml = finalHtml.replace(/\{\{if[^}]*\}\}/g, '');
      finalHtml = finalHtml.replace(/\{\{end\}\}/g, '');

      return finalHtml;
    } catch (error) {
      console.error('Error cleaning HTML content:', error);
      return htmlString;
    }
  };

  const htmlContent = cleanupHtmlContent(template?.htmlTemplateBody || '');

  const renderDesktopTopHeader = () => (
    <div
      style={{
        width: '100%',
        maxWidth: '860px',
        backgroundColor: '#0b353f',
        borderRadius: '10px',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
      }}
    >
      <div style={{ display: 'flex', gap: '6px' }}>
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#FFFFFF',
            }}
          />
        ))}
      </div>
      <div
        style={{
          flex: 1,
          textAlign: 'center',
          color: '#fff',
          fontSize: '14px',
          fontWeight: 500,
        }}
      >
        {`${template?.name} - Desktop Preview`}
      </div>
    </div>
  );

  const renderMobileTopHeader = () => (
    <div
      style={{
        width: '100%',
        maxWidth: '270px',
        backgroundColor: '#0b353f',
        borderRadius: '10px',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          color: '#fff',
          fontSize: '12px',
          fontWeight: 500,
        }}
      >
        Mobile Preview
      </div>
    </div>
  );

  const renderEmailHeader = () => {
    const rowStyle = {
      display: 'flex',
      alignItems: 'center',
      padding: isDesktop ? '6px 12px' : '6px 10px',
      fontSize: isDesktop ? '11px' : '10px',
      backgroundColor: '#ffffff',
      borderBottom: '1px solid #e0e0e0',
    };

    const labelStyle = {
      fontWeight: 600,
      marginRight: 6,
      minWidth: 55,
      color: '#666',
    };

    const valueStyle = {
      flex: 1,
      color: '#777',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };

    const fromValue = `${template?.fromName || 'Hotel'} <${
      template?.fromAddress || 'noreply@hotel.com'
    }>`;

    return (
      <div style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#ffffff' }}>
        <div style={rowStyle}>
          <span style={labelStyle}>To:</span>
          <span style={valueStyle}>Guests</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>From:</span>
          <span style={valueStyle}>{fromValue}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Subject:</span>
          <span style={valueStyle}>{template?.subject || 'Email Subject'}</span>
        </div>
      </div>
    );
  };

  const renderMobileStatusBar = () => {
    if (isDesktop) return null;

    return (
      <div
        style={{
          backgroundColor: '#f8f9fa',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTopLeftRadius: '18px',
          borderTopRightRadius: '18px',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <div style={{ fontSize: '11px', color: '#666' }}>{timeString}</div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <SignalCellularAltIcon style={{ fontSize: 14, color: '#666' }} />
          <WifiIcon style={{ fontSize: 14, color: '#666' }} />
          <BatteryFullIcon
            style={{
              fontSize: 14,
              color: '#666',
              transform: 'rotate(90deg)',
              transformOrigin: 'center',
            }}
          />
        </div>
      </div>
    );
  };

  const extractBgColor = (html) => {
    if (!html) return '#ffffff';
    const match = html.match(/background-color:\s*([^;}"']+)/i);
    return match ? match[1].trim() : '#ffffff';
  };

  const bgColor = extractBgColor(template?.htmlTemplateBody);

  const extractContentWidth = (html) => {
    if (!html) return EMAIL_BASE_WIDTH;
    const match = html.match(/width="(\d+)"[^>]*data-editor-canvas="true"/i);
    return match ? parseInt(match[1], 10) : EMAIL_BASE_WIDTH;
  };

  const contentWidth = extractContentWidth(htmlContent);
  const dynamicMobileScale = MOBILE_CONTENT_WIDTH / contentWidth;
  const desktopScale = bodyContainerWidth > 0 && contentWidth > bodyContainerWidth
    ? bodyContainerWidth / contentWidth
    : 1;

  useEffect(() => {
    if (!isDesktop) return;
    const node = bodyContainerRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setBodyContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [isDesktop]);

  const renderEmailBody = () => {
    const maxHeight = isDesktop ? DESKTOP_BODY_HEIGHT : MOBILE_BODY_HEIGHT;
    const scaledHeight = isDesktop ? contentHeight * desktopScale : contentHeight * dynamicMobileScale;
    const containerHeight = Math.min(maxHeight, scaledHeight);

    const outerClassName = isDesktop
      ? 'email-preview-scroll-outer-desktop'
      : 'email-preview-scroll-outer-mobile';
    const innerClassName = isDesktop
      ? 'email-preview-scroll-inner-desktop'
      : 'email-preview-scroll-inner-mobile';

    return (
      <div
        ref={isDesktop ? bodyContainerRef : undefined}
        className={outerClassName}
        style={{
          height: isDesktop ? `${containerHeight}px` : undefined,
          flex: isDesktop ? undefined : 1,
          minHeight: isDesktop ? undefined : 0,
          overflow: 'hidden',
          backgroundColor: bgColor,
          width: '100%',
          position: 'relative',
        }}
      >
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className={innerClassName}
          style={{
            overflowY: 'auto',
            overflowX: isDesktop ? 'hidden' : 'auto',
            height: '100%',
            backgroundColor: bgColor,
            padding: 0,
            width: '100%',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <style>{`
            .${innerClassName}, .${innerClassName} * {
              scrollbar-width: none !important;
              -ms-overflow-style: none !important;
            }
            .${innerClassName}::-webkit-scrollbar, 
            .${innerClassName} *::-webkit-scrollbar {
              display: none !important;
              width: 0 !important;
              height: 0 !important;
            }
          `}</style>
          <div
            style={{
              width: looksLikeMobile ? `${MOBILE_CONTENT_WIDTH}px` : '100%',
              height: looksLikeMobile
                ? `${contentHeight * dynamicMobileScale}px`
                : `${contentHeight * desktopScale}px`,
              margin: looksLikeMobile ? '0' : '0 auto',
              flexShrink: 0,
              backgroundColor: bgColor,
              position: 'relative',
            }}
          >
            <iframe
              title='Email Preview'
              onLoad={handleIframeLoad}
              style={{
                width: `${contentWidth}px`,
                height: `${contentHeight}px`,
                border: 'none',
                transform: isDesktop
                  ? (desktopScale < 1 ? `scale(${desktopScale})` : 'none')
                  : `scale(${dynamicMobileScale})`,
                transformOrigin: 'top left',
                backgroundColor: bgColor,
                display: 'block',
              }}
              srcDoc={`
                <!DOCTYPE html>
                <html>
                  <head>
                    <meta charset="utf-8">
                    ${!isDesktop ? `<meta name="viewport" content="width=${contentWidth}">` : ''}
                    <style>
                      body {
                        margin: 0;
                        padding: 0;
                        background-color: ${bgColor};
                        overflow-y: auto !important;
                        overflow-x: hidden !important;
                        scrollbar-width: none !important;
                        -ms-overflow-style: none !important;
                        font-family: Arial, sans-serif;
                      }
                      body::-webkit-scrollbar {
                        display: none !important;
                        width: 0 !important;
                        height: 0 !important;
                      }
                      * { box-sizing: border-box; }
                      img { max-width: 100%; height: auto; display: block; }
                    </style>
                  </head>
                  <body>
                    ${htmlContent}
                    <script>
                      function sendHeight() {
                        const height = document.body.scrollHeight;
                        window.parent.postMessage({ type: 'iframeHeight', height: height }, '*');
                      }
                      window.addEventListener('load', sendHeight);
                      new ResizeObserver(sendHeight).observe(document.body);
                    </script>
                  </body>
                </html>
              `}
            />
          </div>
        </div>
      </div>
    );
  };

  if (isDesktop) {
    return (
      <div className='template-preview-root template-preview-root-desktop'>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            position: 'relative',
          }}
        >
          {renderDesktopTopHeader()}
          <div style={{ height: 16 }} />
          <div
            className='template-preview-shell'
            style={{
              width: '100%',
              maxWidth: '860px',
              border: '1px solid #e0e0e0',
              borderRadius: '10px',
              backgroundColor: bgColor,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              height: '540px',
            }}
          >
            {renderEmailHeader()}
            {renderEmailBody()}
          </div>
          {hasVerticalScroll && (
            <div
              className='email-preview-scrollbar-desktop'
              style={{ right: '-8px', top: '140px', bottom: '10px' }}
            >
              <div
                ref={thumbRef}
                className='email-preview-scrollbar-thumb-desktop'
                style={{ backgroundColor: '#03e67c' }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='template-preview-root template-preview-root-mobile'>
      <div
        style={{
          width: '270px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        {renderMobileTopHeader()}
        <div style={{ height: 16 }} />
        <div
          className='template-preview-shell'
          style={{
            width: '100%',
            maxWidth: '270px',
            borderRadius: '25px',
            backgroundColor: bgColor,
            border: '4px solid #8f8f8f',
            borderBottom: '14px solid #8f8f8f',
            boxShadow: '0 3px 9px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            height: '545px',
          }}
        >
          <div style={{ backgroundColor: bgColor, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {renderMobileStatusBar()}
            {renderEmailHeader()}
            {renderEmailBody()}
          </div>
        </div>
        {hasVerticalScroll && (
          <div
            className='email-preview-scrollbar-mobile'
            style={{ right: '-12px', top: '167px', bottom: '25px' }}
          >
            <div
              ref={thumbRef}
              className='email-preview-scrollbar-thumb-mobile'
              style={{ backgroundColor: '#03e67c' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatePreview;
